import { PhoneCall, PhoneSlash, Buildings, IdentificationCard, ArrowsLeftRight, User } from "@phosphor-icons/react";
import { normalizePhone } from "@/lib/phone";

const getInitials = (name) => {
    if (!name) return null;
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const KEYFRAMES = `
@keyframes icm-pop { 0% { opacity: 0; transform: translateY(14px) scale(.94); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes icm-ring { 0% { transform: scale(.85); opacity: .5; } 70% { opacity: 0; } 100% { transform: scale(1.65); opacity: 0; } }
@keyframes icm-sheen { 0% { transform: translateX(-120%); } 100% { transform: translateX(220%); } }
`;

export default function IncomingCallModal({ from, name, company, type, isTransfer, transferFrom, onAccept, onReject }) {
    const initials = getInitials(name);
    const accent = isTransfer ? "#0284c7" : "#059669";        // sky-600 / emerald-600
    const accentSoft = isTransfer ? "#e0f2fe" : "#d1fae5";     // sky-100 / emerald-100

    return (
        <div
            className="fixed inset-0 z-50 grid place-items-center bg-slate-900/35 p-4 backdrop-blur-md"
            data-testid="incoming-call-modal"
        >
            <style>{KEYFRAMES}</style>

            <div
                className="relative w-full max-w-[380px] overflow-hidden rounded-[28px] border border-slate-200 bg-white text-slate-900 shadow-2xl shadow-slate-900/20"
                style={{ animation: "icm-pop .28s cubic-bezier(.2,.8,.2,1) both" }}
            >
                {/* top accent line + sheen */}
                <div className="relative h-1 w-full overflow-hidden" style={{ background: accent }}>
                    <span
                        className="absolute inset-y-0 w-1/3 bg-white/70"
                        style={{ animation: "icm-sheen 2.4s ease-in-out infinite" }}
                    />
                </div>

                {/* soft tinted header behind the avatar */}
                <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-44"
                    style={{ background: `linear-gradient(180deg, ${accentSoft} 0%, rgba(255,255,255,0) 100%)`, opacity: 0.9 }}
                />

                <div className="relative flex flex-col items-center px-7 pb-7 pt-7">
                    {/* status label */}
                    {isTransfer ? (
                        <div
                            className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-sky-700"
                            data-testid="incoming-transfer-badge"
                        >
                            <ArrowsLeftRight size={12} weight="bold" />
                            {transferFrom ? `Transfer from ${transferFrom}` : "Transferred call"}
                        </div>
                    ) : (
                        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                            Incoming call
                        </div>
                    )}

                    {/* avatar with layered pulse rings */}
                    <div className="relative mb-5 grid h-28 w-28 place-items-center">
                        {[0, 0.6, 1.2].map((d) => (
                            <span
                                key={d}
                                className="absolute inset-0 rounded-full"
                                style={{ border: `1.5px solid ${accent}`, animation: `icm-ring 2.4s ease-out ${d}s infinite` }}
                            />
                        ))}
                        <div
                            className="relative z-10 grid h-24 w-24 place-items-center rounded-full bg-white"
                            style={{ border: `2px solid ${accent}`, boxShadow: `0 10px 30px -10px ${accent}` }}
                        >
                            {initials ? (
                                <span className="font-heading text-3xl font-bold tracking-tight" style={{ color: accent }}>{initials}</span>
                            ) : (
                                <User size={34} weight="fill" style={{ color: accent }} />
                            )}
                        </div>
                    </div>

                    {/* CRM identity chip */}
                    {type && (
                        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">
                            <IdentificationCard size={12} weight="fill" />
                            {type === "Lead" ? "Salesforce Lead" : "Salesforce Contact"}
                        </div>
                    )}

                    <div className="text-center font-heading text-[26px] font-bold leading-tight tracking-tight text-slate-900" data-testid="incoming-name">
                        {name || "Unknown caller"}
                    </div>
                    {company && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500" data-testid="incoming-company">
                            <Buildings size={14} weight="fill" /> {company}
                        </div>
                    )}
                    <div className="mt-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono text-sm tabular text-slate-600" data-testid="incoming-from">
                        {normalizePhone(from) || "Unknown number"}
                    </div>

                    {/* actions */}
                    <div className="mt-8 grid w-full grid-cols-2 gap-3">
                        <button
                            data-testid="reject-incoming"
                            onClick={onReject}
                            className="group flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-white py-3.5 font-heading text-sm font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-[0.97]"
                        >
                            <PhoneSlash size={18} weight="fill" className="transition-transform group-hover:-rotate-12" /> Decline
                        </button>
                        <button
                            data-testid="accept-incoming"
                            onClick={onAccept}
                            className="group flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3.5 font-heading text-sm font-semibold text-white shadow-lg shadow-emerald-600/30 transition-all hover:bg-emerald-500 active:scale-[0.97]"
                        >
                            <PhoneCall size={18} weight="fill" className="transition-transform group-hover:rotate-12" /> Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
